import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import {
    type NodeProps,
    type Node,
    Handle,
    Position,
    useReactFlow,
    useNodes,
    useEdges,
} from "@xyflow/react";
import {
    BrainCircuit,
    Trash2,
    X,
    Settings,
    Database,
    AlertTriangle,
    Loader2,
    CheckCircle,
    PlayCircle,
    FileText,
    MessageSquare,
    Bot,
    Code,
    Package,
    SlidersHorizontal,
} from "lucide-react";
import { getNodeValidationStatus } from "../../utils/nodeValidation";
import { formatDragVariable } from "../../utils/variableHelper";

import { SchemaBuilderModal } from "./schemaBuilderModal";
import { getAllAncestorNodes } from "../../utils/workflowHelpers";
import { NodeDataSidebar } from "../../common/shared/NodeDataSidebar";
import { PromptEditorModal } from "../../common/ui/PromptEditorModal";
import NodeDocsButton from "../../docs/NodeDocsButton";

import { AvailableVariable } from "../../common/ui/types";

import syntaxHighlight from "../../utils/jsonViewer";

export const GoogleUnderstandingNode: React.FC<NodeProps<Node<any>>> = ({
    id,
    data,
    selected,
}) => {
    // Input States
    const [files, setFiles] = useState<string>(data?.files || "");
    const [prompt, setPrompt] = useState<string>(data?.prompt || "");
    const [modelName, setModelName] = useState<string>(data?.model_name || "");
    const [additionalContent, setAdditionalContent] = useState<string>(
        data?.additional_content || ""
    );

    // Schema State
    const [schema, setSchema] = useState<any>(data?.schema || {});

    // AI Config State
    const [aiConfig, setAiConfig] = useState<{
        temperature?: number;
        topP?: number;
        topK?: number;
        maxOutputTokens?: number;
        thinkingBudget?: number;
    }>(data?.ai_config || {});

    // UI States
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalTab, setModalTab] = useState<"config" | "advanced">("config");
    const [isSchemaModalOpen, setIsSchemaModalOpen] = useState(false);
    const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    const { setNodes, deleteElements, getNodes, getEdges } = useReactFlow();
    const nodes = useNodes();
    const edges = useEdges();

    // Execution Status
    const executionStatus = data?.executionStatus || null;
    const isExecuting = executionStatus === "running";
    const executionSuccess = executionStatus === "success";
    const executionError = executionStatus === "error";

    // Build list of previous nodes for the sidebar (Ancestors)
    const previousNodes = useMemo(() => {
        const ancestors = getAllAncestorNodes(id, nodes, edges);

        return ancestors.map((n) => ({
            nodeId: n.id,
            nodeName: n.data?.label || n.data?.name || n.id,
            nodeType: n.type || "unknown",
            inputs: n.data?.inputs || {},
            outputs: n.data?.outputs || {},
        })) as AvailableVariable[];
    }, [edges, id, nodes]);

    // Update Node Data & Validate
    useEffect(() => {
        const currentNodes = getNodes();
        const currentEdges = getEdges();
        const currentNode = currentNodes.find((n) => n.id === id);

        if (!currentNode) return;

        const newData = {
            ...currentNode.data,
            schema,
            files,
            prompt,
            model_name: modelName,
            additional_content: additionalContent,
            ai_config: aiConfig,
        };

        setNodes((nds) =>
            nds.map((node) => {
                if (node.id !== id) return node;
                return {
                    ...node,
                    data: newData,
                };
            })
        );

        const { errors: validationErrors } = getNodeValidationStatus(
            { ...currentNode, data: newData },
            currentEdges,
            currentNodes
        );
        setErrors(validationErrors);
    }, [
        files,
        prompt,
        modelName,
        additionalContent,
        schema,
        aiConfig,
        id,
        setNodes,
        getNodes,
        getEdges,
    ]);

    const handleDeleteNode = useCallback(() => {
        deleteElements({ nodes: [{ id }] });
    }, [deleteElements, id]);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "copy";
        setIsDraggingOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.currentTarget.contains(e.relatedTarget as unknown as globalThis.Node))
            return;
        setIsDraggingOver(false);
    };

    const handleDrop = (e: React.DragEvent, targetField: string) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(false);

        try {
            const fullVariable = e.dataTransfer.getData("text/plain");
            if (!fullVariable) return;

            switch (targetField) {
                case "files":
                    setFiles(fullVariable);
                    break;
                case "additional_content":
                    setAdditionalContent(fullVariable);
                    break;
                case "prompt":
                    setPrompt((prev) => (prev ? `${prev}\n${fullVariable}` : fullVariable));
                    break;
                case "model_name":
                    setModelName(fullVariable);
                    break;
            }
        } catch (err) {
            console.error("Failed to parse dropped data", err);
        }
    };

    const handleModalDragStart = (
        e: React.DragEvent,
        variable: string,
        nodeName: string,
        type: "input" | "output",
    ) => {
        e.stopPropagation();
        const fullVariable = formatDragVariable(nodeName, variable, type);
        e.dataTransfer.setData("text/plain", fullVariable);
        e.dataTransfer.effectAllowed = "copy";
    };

    return (
        <>
            <div
                className={`relative bg-white border-2 rounded-xl min-w-[300px] shadow-sm transition-all duration-200 group
          ${errors.length > 0 && !isExecuting
                        ? "border-red-400"
                        : isExecuting
                            ? "border-blue-500 shadow-lg shadow-blue-200 animate-pulse"
                            : executionSuccess
                                ? "border-green-500 shadow-md shadow-green-200"
                                : executionError
                                    ? "border-red-500 shadow-md shadow-red-200"
                                    : selected
                                        ? "border-blue-500 shadow-lg scale-105"
                                        : "border-gray-200 hover:border-blue-300"
                    }
          ${isDraggingOver ? "ring-2 ring-violet-400 ring-offset-2" : ""}
        `}
            >
                {/* Execution Spinner */}
                {isExecuting && (
                    <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
                        <div className="bg-white rounded-full p-3 shadow-lg">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                    </div>
                )}

                {/* Success Overlay */}
                {executionSuccess && (
                   <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
                             <div className="bg-green-500 rounded-full p-1.5 border-2 border-white">
                               <CheckCircle className="w-4 h-4 text-white" />
                             </div>
                           </div>
                )}

                {/* Error Overlay */}
                {executionError && !isExecuting && (
                     <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
                              <div className="bg-red-500 rounded-full p-1.5 border-2 border-white">
                                <AlertTriangle className="w-4 h-4 text-white" />
                              </div>
                            </div>
                )}

                {/* Execute Button */}
                <div className="absolute -top-3 -right-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            data.onExecuteNode?.(id);
                        }}
                        className="p-1.5 bg-blue-500 text-white rounded-full shadow-md hover:bg-blue-600 transition-all"
                        title="Execute all nodes up to this node"
                    >
                        {isExecuting ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                            <PlayCircle className="w-3 h-3" />
                        )}
                    </button>
                </div>

                {/* Header */}
                <div
                    className={`p-3 rounded-t-xl flex items-center justify-between
             ${executionSuccess
                            ? "bg-green-50"
                            : executionError
                                ? "bg-red-50"
                                : "bg-gradient-to-r from-violet-50 to-purple-50"
                        }
        `}
                >
                    <div className="flex items-center gap-2">
                        <div
                            className={`p-2 rounded-lg ${executionSuccess
                                ? "bg-green-100 text-green-600"
                                : executionError
                                    ? "bg-red-100 text-red-600"
                                    : "bg-white text-violet-600 shadow-sm"
                                }`}
                        >
                            {isExecuting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <BrainCircuit className="w-4 h-4" />
                            )}
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 text-sm">
                                Smart Extraction (Gemini)
                            </h3>
                            <p className="text-[10px] text-gray-500">
                                AI-Powered Data Extraction
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-1 items-center">
                        <NodeDocsButton nodeType="GEMINI-UNDERSTANDING" />
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteNode();
                            }}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-3 space-y-3">
                    {/* Configure Button */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsModalOpen(true);
                        }}
                        className="w-full py-2.5 rounded-lg bg-violet-600 text-white text-xs font-medium flex justify-center gap-2 shadow-sm hover:bg-violet-700 transition-colors"
                    >
                        <Settings className="w-3.5 h-3.5" /> Configure Node
                    </button>

                    {/* Inputs Display */}
                    <div className="space-y-2">
                        <div className="bg-gray-50 rounded-lg p-2 space-y-2 border border-gray-100">
                            <div className="flex justify-between items-center mb-1">
                                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                                    Inputs
                                </p>
                                <p
                                    className={`text-[10px] font-medium ${Object.keys(schema).length > 0
                                        ? "text-green-600"
                                        : "text-gray-400"
                                        }`}
                                >
                                    {Object.keys(schema).length > 0
                                        ? "Schema Configured"
                                        : "Schema (Optional)"}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 gap-2">
                                {files && (
                                    <div className="text-[10px] bg-white border px-2 py-1 rounded text-gray-700 truncate flex items-center gap-1">
                                        <FileText className="w-3 h-3 text-blue-500" /> {files}
                                    </div>
                                )}
                                {modelName && (
                                    <div className="text-[10px] bg-white border px-2 py-1 rounded text-gray-700 truncate flex items-center gap-1">
                                        <Bot className="w-3 h-3 text-violet-500" /> {modelName}
                                    </div>
                                )}
                                {additionalContent && (
                                    <div className="text-[10px] bg-white border px-2 py-1 rounded text-gray-700 truncate">
                                        Extra: {additionalContent}
                                    </div>
                                )}

                                {!files && !modelName && !additionalContent && (
                                    <div className="text-[10px] text-gray-400 italic text-center py-2">
                                        Configure inputs in settings
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Validation Errors */}
                {!selected && errors.length > 0 && (
                    <div className="absolute -bottom-8 left-0 right-0 flex justify-center">
                        <div className="bg-red-100 text-red-600 text-[10px] px-2 py-1 rounded-full shadow-sm flex items-center gap-1 border border-red-200">
                            <AlertTriangle className="w-3 h-3" />
                            {errors.length} Error{errors.length > 1 ? "s" : ""}
                        </div>
                    </div>
                )}

                {/* Handles */}
                <Handle
                    type="target"
                    position={Position.Left}
                    className="w-3 h-3 bg-violet-600 border-2 border-white"
                    style={{
                        boxShadow:
                            "0 0 0 4px rgba(154, 8, 252, 0.2), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
                    }}
                />
                <Handle
                    type="source"
                    position={Position.Right}
                    className="w-3 h-3 bg-violet-600 border-2 border-white"
                    style={{
                        boxShadow:
                            "0 0 0 4px rgba(154, 8, 252, 0.2), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
                    }}
                />
                <Handle
                    type="source"
                    id="error"
                    position={Position.Bottom}
                    className="w-3 h-3 bg-red-500 border-2 border-white"
                    style={{
                        boxShadow: "0 0 0 4px rgba(239, 68, 68, 0.25)",
                    }}
                />
            </div>

            {/* Schema Builder Modal */}
            <SchemaBuilderModal
                isOpen={isSchemaModalOpen}
                onClose={() => setIsSchemaModalOpen(false)}
                initialSchema={schema}
                onSave={(newSchema) => setSchema(newSchema)}
            />

            {/* Prompt Editor Modal */}
            <PromptEditorModal
                isOpen={isPromptModalOpen}
                onClose={() => setIsPromptModalOpen(false)}
                value={prompt}
                onChange={setPrompt}
                title="Edit Extraction Instructions"
                description="Provide specific instructions for how the AI should extract data"
                placeholder="Enter your extraction instructions here..."
            />

            {/* Main Configuration Modal */}
            {isModalOpen &&
                ReactDOM.createPortal(
                    <div
                        className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
                        onClick={() => setIsModalOpen(false)}
                    >
                        <div
                            className="bg-white rounded-lg shadow-2xl w-[95vw] max-w-[1600px] h-[92vh] overflow-hidden flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-violet-100 rounded-lg">
                                        <BrainCircuit className="w-5 h-5 text-violet-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-semibold text-gray-900">
                                            Smart Extraction (Gemini) Configuration
                                        </h2>
                                        <p className="text-xs text-gray-500">
                                            Configure inputs, schema, and AI settings.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Content (3 Columns) */}
                            <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
                                {/* Column 1: Left Sidebar (Previous Nodes) */}
                                <div className="w-[350px] shrink-0 overflow-hidden">
                                    <NodeDataSidebar
                                        availableVariables={previousNodes}
                                        onDragStart={handleModalDragStart}
                                    />
                                </div>

                                {/* Column 2: Center - Inputs & Schema */}
                                <div className="flex-1 bg-white flex flex-col overflow-hidden">
                                    {/* Tabs */}
                                    <div className="flex items-center border-b border-gray-200 bg-gray-50/50 px-6">
                                        <button
                                            onClick={() => setModalTab("config")}
                                            className={`px-4 py-3 text-xs font-medium transition-colors border-b-2 flex items-center gap-1.5 cursor-pointer ${
                                                modalTab === "config"
                                                    ? "border-violet-500 text-violet-700 bg-violet-50/50"
                                                    : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100/50"
                                            }`}
                                        >
                                            <Settings className="w-3.5 h-3.5" />
                                            Configuration
                                        </button>
                                        <button
                                            onClick={() => setModalTab("advanced")}
                                            className={`px-4 py-3 text-xs font-medium transition-colors border-b-2 flex items-center gap-1.5 cursor-pointer ${
                                                modalTab === "advanced"
                                                    ? "border-violet-500 text-violet-700 bg-violet-50/50"
                                                    : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100/50"
                                            }`}
                                        >
                                            <SlidersHorizontal className="w-3.5 h-3.5" />
                                            Advanced Configuration
                                        </button>
                                    </div>

                                    <div className="flex-1 px-2 py-6 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">

                                    {modalTab === "config" && (
                                    <div className="mx-4 space-y-8">
                                        {/* Input Configuration Section */}
                                        <div>
                                            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                                <div className="p-1.5 bg-violet-100 rounded text-violet-600">
                                                    <Database className="w-4 h-4" />
                                                </div>
                                                Input Mapping
                                            </h3>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {/* Files Input */}
                                                <div className="md:col-span-2 space-y-1">
                                                    <label className="text-xs font-medium text-gray-700">
                                                        Files{" "}
                                                        <span className="text-gray-400 font-normal">(Required)</span>
                                                    </label>
                                                    <p className="text-[10px] text-gray-400 mb-1.5">
                                                        Drag file array or file object here.
                                                    </p>
                                                    <div
                                                        onDrop={(e) => handleDrop(e, "files")}
                                                        onDragOver={handleDragOver}
                                                        onDragLeave={handleDragLeave}
                                                        className={`h-12 border-2 border-dashed rounded-lg flex items-center px-3 transition-colors ${files
                                                            ? "border-green-300 bg-green-50"
                                                            : "border-gray-300 bg-gray-50 hover:border-violet-300"
                                                            }`}
                                                    >
                                                        {files ? (
                                                            <span className="text-xs font-mono text-green-700 truncate w-full pointer-events-none">
                                                                {files}
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs text-gray-400 pointer-events-none">
                                                                Drag files here (e.g. from File Upload Node)
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Additional Content Input */}
                                                <div className="md:col-span-2 space-y-1">
                                                    <label className="text-xs font-medium text-gray-700">
                                                        Additional Content{" "}
                                                        <span className="text-gray-400 font-normal">(Optional)</span>
                                                    </label>
                                                    <p className="text-[10px] text-gray-400 mb-1.5">
                                                        Drag any additional metadata or context here.
                                                    </p>
                                                    <div
                                                        onDrop={(e) => handleDrop(e, "additional_content")}
                                                        onDragOver={handleDragOver}
                                                        onDragLeave={handleDragLeave}
                                                        className={`h-12 border-2 border-dashed rounded-lg flex items-center px-3 transition-colors ${additionalContent
                                                            ? "border-green-300 bg-green-50"
                                                            : "border-gray-300 bg-gray-50 hover:border-violet-300"
                                                            }`}
                                                    >
                                                        {additionalContent ? (
                                                            <span className="text-xs font-mono text-green-700 truncate w-full pointer-events-none">
                                                                {additionalContent}
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs text-gray-400 pointer-events-none">
                                                                Drag additional content here
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Model Name Input */}
                                                <div className="md:col-span-2 space-y-1">
                                                    <label className="text-xs font-medium text-gray-700">
                                                        Model Name{" "}
                                                        <span className="text-gray-400 font-normal">(Required)</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={modelName}
                                                        onChange={(e) => setModelName(e.target.value)}
                                                        placeholder="e.g. gemini-1.5-flash"
                                                        className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-violet-500 transition-colors"
                                                    />
                                                </div>

                                                {/* Prompt Input */}
                                                <div className="md:col-span-2 space-y-1">
                                                    <label className="text-xs font-medium text-gray-700">
                                                        Prompt{" "}
                                                        <span className="text-gray-400 font-normal">(Required)</span>
                                                    </label>
                                                    <p className="text-[10px] text-gray-400 mb-1.5">
                                                        Instructions for the AI model.
                                                    </p>
                                                    <div
                                                        onClick={() => setIsPromptModalOpen(true)}
                                                        onDrop={(e) => handleDrop(e, "prompt")}
                                                        onDragOver={handleDragOver}
                                                        onDragLeave={handleDragLeave}
                                                        className={`w-full min-h-[100px] p-3 border border-gray-300 text-xs font-mono text-gray-700 bg-gray-50 hover:bg-white hover:border-violet-400 cursor-pointer transition-all group relative ${isDraggingOver ? "ring-2 ring-violet-400 bg-violet-50/30" : ""
                                                            }`}
                                                    >
                                                        {prompt ? (
                                                            <div className="whitespace-pre-wrap break-words line-clamp-4">
                                                                {prompt}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-400 italic">
                                                                Click to edit prompt or drag text here...
                                                            </span>
                                                        )}
                                                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <span className="bg-white/80 backdrop-blur-sm p-1.5 rounded-md shadow-sm border border-gray-200 text-[10px] font-medium text-violet-600 flex items-center gap-1">
                                                                <MessageSquare className="w-3 h-3" /> Edit
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>

                                        <div className="border-t border-gray-200 pt-8">
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                                                    <div className="p-1.5 bg-blue-100 rounded text-blue-600">
                                                        <Code className="w-4 h-4" />
                                                    </div>
                                                    Schema Definition
                                                </h3>
                                                <button
                                                    onClick={() => setIsSchemaModalOpen(true)}
                                                    className="px-3 py-1.5 bg-violet-600 text-white text-xs font-medium rounded-lg hover:bg-violet-700 transition-colors border border-violet-100 flex items-center gap-1.5"
                                                >
                                                    <Settings className="w-3.5 h-3.5" />
                                                    Configure Schema
                                                </button>
                                            </div>

                                            <div className="bg-white rounded-md p-4 shadow-sm h-[300px] w-full border border-gray-200 overflow-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
                                                {Object.keys(schema).length > 0 ? (
                                                    <pre
                                                        className="text-xs font-mono leading-relaxed pointer-events-none"
                                                        dangerouslySetInnerHTML={{
                                                            __html: syntaxHighlight(schema),
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                                                        <Code className="w-8 h-8 opacity-20" />
                                                        <p className="text-sm font-medium">
                                                            No schema defined.
                                                        </p>
                                                        <p className="text-xs opacity-70">
                                                            Click "Open Schema Builder" to configure.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                    </div>
                                    )}

                                    {modalTab === "advanced" && (
                                    <div className="mx-4 space-y-6">
                                        <div>
                                            <h3 className="text-base font-semibold text-gray-900 mb-1 flex items-center gap-2">
                                                <div className="p-1.5 bg-violet-100 rounded text-violet-600">
                                                    <SlidersHorizontal className="w-4 h-4" />
                                                </div>
                                                Advanced Configuration
                                            </h3>
                                            <p className="text-xs text-gray-500 mb-6">
                                                Fine-tune the AI model parameters. Leave empty to use default values.
                                            </p>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                {/* Temperature */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-medium text-gray-700 flex items-center justify-between">
                                                        Temperature
                                                        <span className="text-[10px] text-gray-400 font-normal">Default: 0.5</span>
                                                    </label>
                                                    <p className="text-[10px] text-gray-400">
                                                        Controls randomness. Lower = more focused, higher = more creative.
                                                    </p>
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={aiConfig.temperature ?? ""}
                                                        onChange={(e) => { const v = e.target.value; if (v === "" || /^\d*\.?\d*$/.test(v)) setAiConfig(prev => ({ ...prev, temperature: v ? parseFloat(v) : undefined })); }}
                                                        placeholder="0.5"
                                                        className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-violet-500 transition-colors"
                                                    />
                                                </div>

                                                {/* Top P */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-medium text-gray-700 flex items-center justify-between">
                                                        Top P
                                                        <span className="text-[10px] text-gray-400 font-normal">Default: 0.1</span>
                                                    </label>
                                                    <p className="text-[10px] text-gray-400">
                                                        Nucleus sampling. Controls diversity of token selection.
                                                    </p>
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={aiConfig.topP ?? ""}
                                                        onChange={(e) => { const v = e.target.value; if (v === "" || /^\d*\.?\d*$/.test(v)) setAiConfig(prev => ({ ...prev, topP: v ? parseFloat(v) : undefined })); }}
                                                        placeholder="0.1"
                                                        className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-violet-500 transition-colors"
                                                    />
                                                </div>

                                                {/* Top K */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-medium text-gray-700 flex items-center justify-between">
                                                        Top K
                                                        <span className="text-[10px] text-gray-400 font-normal">Default: 1</span>
                                                    </label>
                                                    <p className="text-[10px] text-gray-400">
                                                        Limits token selection to top K candidates.
                                                    </p>
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        value={aiConfig.topK ?? ""}
                                                        onChange={(e) => { const v = e.target.value; if (v === "" || /^\d+$/.test(v)) setAiConfig(prev => ({ ...prev, topK: v ? parseInt(v) : undefined })); }}
                                                        placeholder="1"
                                                        className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-violet-500 transition-colors"
                                                    />
                                                </div>

                                                {/* Max Output Tokens */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-medium text-gray-700 flex items-center justify-between">
                                                        Max Output Tokens
                                                        <span className="text-[10px] text-gray-400 font-normal">Default: 60,000</span>
                                                    </label>
                                                    <p className="text-[10px] text-gray-400">
                                                        Maximum number of tokens in the response.
                                                    </p>
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        value={aiConfig.maxOutputTokens ?? ""}
                                                        onChange={(e) => { const v = e.target.value; if (v === "" || /^\d+$/.test(v)) setAiConfig(prev => ({ ...prev, maxOutputTokens: v ? parseInt(v) : undefined })); }}
                                                        placeholder="60000"
                                                        className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-violet-500 transition-colors"
                                                    />
                                                </div>

                                                {/* Thinking Budget */}
                                                <div className="md:col-span-2 space-y-1.5">
                                                    <label className="text-xs font-medium text-gray-700 flex items-center justify-between">
                                                        Thinking Budget
                                                        <span className="text-[10px] text-gray-400 font-normal">Default: 6,000</span>
                                                    </label>
                                                    <p className="text-[10px] text-gray-400">
                                                        Token budget allocated for the model's internal reasoning process.
                                                    </p>
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        value={aiConfig.thinkingBudget ?? ""}
                                                        onChange={(e) => { const v = e.target.value; if (v === "" || /^\d+$/.test(v)) setAiConfig(prev => ({ ...prev, thinkingBudget: v ? parseInt(v) : undefined })); }}
                                                        placeholder="6000"
                                                        className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-violet-500 transition-colors"
                                                    />
                                                </div>
                                            </div>

                                            {/* Current Config Summary */}
                                            <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                                                <h4 className="text-xs font-semibold text-gray-700 mb-3">Current Configuration</h4>
                                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                                    <div className="bg-white border border-gray-100 rounded-md px-3 py-2">
                                                        <p className="text-[10px] text-gray-400">Temperature</p>
                                                        <p className="text-sm font-semibold text-gray-800">{aiConfig.temperature ?? 0.5}</p>
                                                    </div>
                                                    <div className="bg-white border border-gray-100 rounded-md px-3 py-2">
                                                        <p className="text-[10px] text-gray-400">Top P</p>
                                                        <p className="text-sm font-semibold text-gray-800">{aiConfig.topP ?? 0.1}</p>
                                                    </div>
                                                    <div className="bg-white border border-gray-100 rounded-md px-3 py-2">
                                                        <p className="text-[10px] text-gray-400">Top K</p>
                                                        <p className="text-sm font-semibold text-gray-800">{aiConfig.topK ?? 1}</p>
                                                    </div>
                                                    <div className="bg-white border border-gray-100 rounded-md px-3 py-2">
                                                        <p className="text-[10px] text-gray-400">Max Tokens</p>
                                                        <p className="text-sm font-semibold text-gray-800">{(aiConfig.maxOutputTokens ?? 60000).toLocaleString()}</p>
                                                    </div>
                                                    <div className="bg-white border border-gray-100 rounded-md px-3 py-2">
                                                        <p className="text-[10px] text-gray-400">Think Budget</p>
                                                        <p className="text-sm font-semibold text-gray-800">{(aiConfig.thinkingBudget ?? 6000).toLocaleString()}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    )}

                                    </div>
                                </div>
                                {/* Column 3: Outputs */}
                                <div className="w-[480px] bg-gray-50 p-5 overflow-y-auto border-l flex-shrink-0 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
                                    <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                        <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                                        Execution Output
                                    </h3>

                                    <div className="space-y-3">
                                        <div className="bg-white border p-3 rounded-lg shadow-sm">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-xs text-gray-500">Status</span>
                                                <span
                                                    className={`text-xs font-bold px-2 py-0.5 rounded-full ${executionSuccess
                                                        ? "bg-green-100 text-green-700"
                                                        : executionError
                                                            ? "bg-red-100 text-red-700"
                                                            : isExecuting
                                                                ? "bg-blue-100 text-blue-700"
                                                                : "bg-gray-100 text-gray-600"
                                                        }`}
                                                >
                                                    {executionStatus?.toUpperCase() || "IDLE"}
                                                </span>
                                            </div>
                                            {executionError && data.error && (
                                                <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded border border-red-100">
                                                    {data.error}
                                                </div>
                                            )}
                                        </div>

                                        {data?.outputs && Object.keys(data.outputs).length > 0 ? (
                                            Object.entries(data.outputs).map(
                                                ([key, value]: [string, any]) => (
                                                    <div
                                                        key={key}
                                                        className="bg-white border rounded-lg shadow-sm overflow-hidden"
                                                    >
                                                        <div className="px-3 py-2 flex items-center justify-between border-b border-gray-100 bg-gray-50">
                                                            <span className="text-[10px] font-bold text-gray-500 uppercase">
                                                                {key}
                                                            </span>
                                                            <span className="text-[10px] text-gray-400 font-mono">
                                                                {Array.isArray(value) ? "Array" : typeof value}
                                                            </span>
                                                        </div>
                                                        <div className="p-2 overflow-x-auto">
                                                            <pre
                                                                className="text-xs font-mono break-all whitespace-pre-wrap"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: syntaxHighlight(value),
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                ),
                                            )
                                        ) : (
                                            <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-lg">
                                                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                                    <Package className="w-4 h-4 text-gray-400" />
                                                </div>
                                                <p className="text-xs text-gray-400 italic">
                                                    No outputs generated yet.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </div>

                            {/* Footer */}
                            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 rounded-b-lg">
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 hover:text-gray-900 shadow-sm transition-all"
                                >
                                    Close
                                </button>
                            </div>

                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
};
